/**
 * LensRecordPanel — the read-only record, shown in the right column of the workspace shell when
 * a document is opened from Lens search (S5).
 *
 * WHY THIS IS A SEPARATE PANEL AND NOT `ValidationPanel` WITH EVERYTHING DISABLED.
 * The accept criterion for S5 is that a document opened through Lens is byte-identical afterwards,
 * with "no edit affordance rendered — including no disabled-looking input a clerk could mistake
 * for one". Disabling the editing tree would leave every text box, Submit button and stage stepper
 * mounted and one prop away from live, and `StageManager` would still run its pipeline load and
 * stage machine. Swapping the panel means the editing components are NEVER MOUNTED: there is no
 * input to disable, no submit handler in the tree, and nothing to regress into an editable state.
 *
 * The shell around it (two-column responsive grid, `PDFViewerPanel`, error boundaries, the HITL
 * context providers) is reused unchanged — this is a variant of the workspace, not a second
 * document renderer.
 *
 * Field display reuses `ReadOnlyField`, the same component the review workspace uses for its
 * reference fields, so click-to-highlight-in-PDF behaves identically on both surfaces.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import HistoryIcon from '@mui/icons-material/History';

import ReadOnlyField from './ReadOnlyField';
import StatusBadge from '../shared/StatusBadge';
import { EmptyState } from '../shared/EmptyState';
import { DataTable, DataTableColumn } from '../shared/DataTable';
import { ExtractedDocumentData, ExtractedField } from '../../types';
import type {
  AnalyzerConfiguration,
  FieldConfiguration,
  FieldValueProvenance,
} from '../../services/analyzerService';
import { useHighlighting } from '../../contexts/HighlightingContext';
import type { WorkspaceDocumentRecord } from '../../hooks/useWorkspaceDocument';

/** One row of the correction history table. */
type CorrectionRow = NonNullable<NonNullable<WorkspaceDocumentRecord['validation']>['corrections']>[number];

export interface LensRecordPanelProps {
  extractedData: ExtractedDocumentData;
  /** The stored record — supplies the correction history and the delivered-status facts. */
  record: WorkspaceDocumentRecord | null;
  /**
   * This document type's analyzer config. THREE states, deliberately:
   * `undefined` = not resolved (still loading, or the load failed) — provenance badges are
   * suppressed entirely rather than guessed; `null` = resolved, but this tenant has no config for
   * the type; an object = resolved. Two booleans encoding three states had to be kept in sync by
   * hand across three files.
   */
  analyzerConfig: AnalyzerConfiguration | null | undefined;
}

/**
 * Resolve which analyzer field a stored correction belongs to.
 *
 * `ValidationCorrection.fieldId` is a POSITIONAL synthetic (`field-${index}`) minted per document
 * load, so it names a slot in `extractedFields`, not a field. S5 persists `fieldName` alongside it,
 * but every record corrected before S5 has only the positional id.
 *
 * Order: the stored name (authoritative) → positional resolution against the CURRENT field array
 * (best effort, correct only while the order is unchanged) → null, meaning "we cannot say". Null
 * is a real answer here and is rendered as such; an audit surface must not invent an attribution.
 */
function resolveCorrectionFieldName(
  correction: { fieldId: string; fieldName?: string },
  fields: ExtractedField[]
): string | null {
  if (correction.fieldName) return correction.fieldName;

  const positional = /^field-(\d+)$/.exec(correction.fieldId);
  if (positional) {
    const index = Number(positional[1]);
    return fields[index]?.name ?? null;
  }
  return null;
}

/**
 * Resolve the provenance to show for one field's CURRENT value, or `null` when it is not known.
 *
 * Config states a static per-field truth (`extracted` / `derived` / `enriched`, D8). A value a
 * reviewer typed over during HITL is a per-VALUE fact config cannot express — R1 will persist it,
 * but the document's own `validation.corrections` already records exactly that, so a corrected
 * field reports `'operator'`.
 *
 * Returns `null` when the field is absent from config, or when config never loaded. That is
 * deliberately NOT defaulted to `'extracted'`: "config says extracted", "field is not in config"
 * and "config failed to load" are three different facts, and collapsing them into a confident
 * badge makes a claim about the document the app cannot support. Same principle the search columns
 * already settled (`utils/searchResultColumns.ts`) — admitting a gap beats a confident false claim.
 */
function resolveFieldProvenance(
  fieldName: string,
  correctedFieldNames: Set<string>,
  configByFieldName: Map<string, FieldConfiguration> | null | undefined
): FieldValueProvenance | null {
  if (correctedFieldNames.has(fieldName)) return 'operator';
  if (!configByFieldName) return null;
  return configByFieldName.get(fieldName)?.provenance ?? null;
}

// Hoisted: `DataTable` calls `getCellText` once per cell on every render and is not memoized, so a
// per-row `toLocaleString` would rebuild the formatter for every correction on every render. Same
// reason `utils/searchResultColumns.ts` hoists its Intl formatters (S4).
const CORRECTION_TIMESTAMP_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const formatTimestamp = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : CORRECTION_TIMESTAMP_FORMAT.format(parsed);
};

const LensRecordPanel: React.FC<LensRecordPanelProps> = ({
  extractedData,
  record,
  analyzerConfig,
}) => {
  const { actions: highlightingActions, state: highlightingState } = useHighlighting();

  // Draw the bounding boxes on the PDF.
  //
  // WITHOUT THIS, LENS HIGHLIGHTS NOTHING. `HighlightOverlay` renders from
  // `HighlightingContext.highlightedRegions`, and the ONLY thing that populates that is
  // `showAllFields` — which was called from `DataValidationStage` alone, a component Lens
  // deliberately never mounts. `ReadOnlyField`'s click calls `setActiveField`, which only flips
  // which region is emphasised; it cannot create regions. So the whole point of the split view —
  // seeing WHERE on the page a stored value came from — was silently absent on this surface.
  //
  // Mirrors the initialization in `DataValidationStage`, including the ref guard: `actions` is
  // rebuilt per render, so an unguarded effect re-dispatches on every unrelated re-render.
  const lastInitializedRef = useRef<{ viewport: typeof highlightingState.pdfViewport; fields: ExtractedField[] } | null>(null);

  useEffect(() => {
    const viewport = highlightingState.pdfViewport;
    const fields = extractedData.fields;
    if (!fields || !viewport) return;

    const last = lastInitializedRef.current;
    if (last && last.viewport === viewport && last.fields === fields) return;
    lastInitializedRef.current = { viewport, fields };

    const fieldsWithBoundingBoxes = fields
      .filter((field) => field.boundingBoxes && field.boundingBoxes.length > 0)
      .map((field) => ({ id: field.id, boundingBoxes: field.boundingBoxes }));

    if (fieldsWithBoundingBoxes.length > 0) {
      highlightingActions.showAllFields(fieldsWithBoundingBoxes);
    }
  }, [extractedData.fields, highlightingState.pdfViewport, highlightingActions.showAllFields]);

  const corrections = useMemo(() => record?.validation?.corrections ?? [], [record]);

  // `fields` is required on ExtractedDocumentData, so no fallback and no memo — the panel holds no
  // local state, so it re-renders only when props change, which is exactly when this would recompute.
  const fields: ExtractedField[] = extractedData.fields;

  // Keyed by analyzer FIELD NAME, not by the positional `fieldId` — see
  // `resolveCorrectionFieldName` for why the id cannot attribute a correction to a field.
  const correctedFieldNames = useMemo(() => {
    const names = new Set<string>();
    for (const c of corrections) {
      const name = resolveCorrectionFieldName(c, fields);
      if (name) names.add(name);
    }
    return names;
  }, [corrections, fields]);

  // One pass, one Map, serving BOTH the display name and the provenance lookup. Previously the
  // display name came from a Map while provenance did an Array.find over the same list per rendered
  // field — O(fields x configFields) on every render, for data already indexed here.
  const configByFieldName = useMemo(() => {
    if (!analyzerConfig) return undefined;
    const byName = new Map<string, FieldConfiguration>();
    for (const f of analyzerConfig.fields ?? []) byName.set(f.fieldName, f);
    return byName;
  }, [analyzerConfig]);

  const displayNameFor = useCallback(
    (fieldName: string) => configByFieldName?.get(fieldName)?.displayName || fieldName,
    [configByFieldName]
  );

  // Truncated + tooltipped: corrected values carry arbitrary extracted strings (party names,
  // addresses, legal descriptions) that would otherwise set the column width from the longest row.
  const correctionColumns: DataTableColumn<CorrectionRow>[] = useMemo(
    () => [
      {
        id: 'field',
        label: 'Field',
        truncate: true,
        getCellText: (row) => {
          const name = resolveCorrectionFieldName(row, fields);
          // "Unknown field" is the honest answer for a pre-S5 record whose positional id no longer
          // resolves. Showing the raw `field-3` would put an internal identifier in front of a
          // county clerk and imply it means something to them.
          if (!name) return 'Unknown field';
          return displayNameFor(name);
        },
      },
      {
        id: 'originalValue',
        label: 'Extracted',
        truncate: true,
        getCellText: (row) => row.originalValue || '—',
      },
      {
        id: 'correctedValue',
        label: 'Corrected to',
        truncate: true,
        getCellText: (row) => row.correctedValue || '—',
      },
      { id: 'correctedBy', label: 'By', truncate: true, getCellText: (row) => row.correctedBy },
      { id: 'timestamp', label: 'When', getCellText: (row) => formatTimestamp(row.timestamp) },
    ],
    [displayNameFor, fields]
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Panel header — states plainly that this is a read-only view, so a clerk who is used to
          the review workspace is never left wondering why they cannot type. */}
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 1.5, flexShrink: 0 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
          Record
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Archived record, opened from search. This view is read-only — reviewing and correcting
          happens in the review queue.
        </Typography>
      </Paper>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {fields.length === 0 ? (
          <EmptyState
            message="No extracted fields"
            description="This document has no stored extracted data."
            minHeight="40vh"
          />
        ) : (
          <Box component="section" aria-labelledby="lens-fields-heading">
            <Typography
              id="lens-fields-heading"
              variant="subtitle2"
              component="h3"
              sx={{ mb: 1.5, fontWeight: 600 }}
            >
              Extracted fields
            </Typography>

            {/*
              One row per field, rendered ONCE. `ReadOnlyField` owns the label, the value, the
              click-to-highlight behaviour and the transformation preview; Lens supplies the badge
              pair. Earlier this component drew its own label + confidence badge ABOVE a
              ReadOnlyField that drew its own label, its own confidence in a different notation
              (97% vs "High"), and a "Reference" chip belonging to the review workspace's
              reference/new-information split — two labels and two contradictory confidence
              readings per field. The badges/label props exist to keep it to one of each.
            */}
            {fields.map((field) => {
              const provenance = resolveFieldProvenance(
                field.name,
                correctedFieldNames,
                configByFieldName
              );
              return (
                <ReadOnlyField
                  key={field.id}
                  field={field}
                  value={field.value}
                  label={displayNameFor(field.name)}
                  showRequiredMarker={false}
                  badges={
                    <>
                      {/* No badge at all when provenance is unknown — see resolveFieldProvenance. */}
                      {provenance && <StatusBadge type="provenance" provenance={provenance} />}
                      <StatusBadge type="confidence" confidence={field.confidence} />
                    </>
                  }
                />
              );
            })}
          </Box>
        )}

        {/* Correction history — real stored corrections only. When a document was never corrected
            there is nothing to show and we say so, rather than rendering an empty table. */}
        <Divider sx={{ my: 3 }} />
        <Box component="section" aria-labelledby="lens-corrections-heading">
          <Typography
            id="lens-corrections-heading"
            variant="subtitle2"
            component="h3"
            sx={{ mb: 1.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <HistoryIcon fontSize="small" aria-hidden="true" />
            Correction history
          </Typography>

          <DataTable
            columns={correctionColumns}
            rows={corrections}
            getRowId={(row) => `${row.fieldId}-${row.timestamp}`}
            ariaLabel="Correction history"
            emptyMessage="No corrections"
            emptyDescription="No corrections were made to this document."
          />
        </Box>
      </Box>
    </Box>
  );
};

export default LensRecordPanel;
